import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon, XCircle, ChevronDown, XIcon, WandSparkles } from 'lucide-react'

import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { cn } from '@/utilities/ui'

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  'm-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300',
  {
    variants: {
      variant: {
        default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface OptionsType {
  id: string
  post_code: string
  name: string
  wilaya_id: string
  ar_name: string
  longitude: string
  latitude: string
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    id: string
    post_code: string
    name: string
    wilaya_id: string
    ar_name: string
    longitude: string
    latitude: string
  }[]

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void

  /** The initial selected values when the component mounts. */
  initialSelectedValues?: string[]

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string

  /**
   * any.
   * any.
   */
  isPopoverOpen: boolean
  /**
   * any.
   * any.
   */
  setIsPopoverOpen: any
}

export const MultiCommuneSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      initialSelectedValues = [],
      placeholder = 'Select options',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      isPopoverOpen,
      setIsPopoverOpen,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(initialSelectedValues)

    React.useEffect(() => {
      setSelectedValues(initialSelectedValues)
    }, [initialSelectedValues])

    // const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(false)

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true)
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues]
        newSelectedValues.pop()
        setSelectedValues(newSelectedValues)
        onValueChange(newSelectedValues)
      }
    }

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.some((value) => value === option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option]
      setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const handleClear = () => {
      setSelectedValues([])
      onValueChange([])
    }

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev: boolean) => !prev)
    }

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount)
      setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear()
      } else {
        const allValues = options.map((option) => option.id)
        setSelectedValues(allValues)
        onValueChange(allValues)
      }
    }

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild className="!p-0 min-h-[50px] ">
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'flex w-full p-0 rounded-md min-h-[50px] h-auto items-center justify-between bg-inputBackground hover:bg-inherit [&_svg]:pointer-events-auto',
              isPopoverOpen
                ? 'rounded-br-none rounded-bl-none border border-typography'
                : 'border border-border',
              className,
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="min-h-[50px] bg-inputBackground rounded-xl flex justify-between items-center w-full">
                <div className="multiSelectField flex items-center overflow-x-auto ">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.id === value)
                    // const IconComponent = option?.icon
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          isAnimating ? 'animate-bounce' : '',
                          multiSelectVariants({ variant }),
                          'py-1 text-typography',
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {/* {IconComponent && <IconComponent className="h-4 w-4 mr-2" />} */}
                        {option?.name}
                        <XCircle
                          className="text-typography ml-2 h-4 w-4 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation()
                            toggleOption(value)
                          }}
                        />
                      </Badge>
                    )
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-transparent text-typography border-primary hover:bg-transparent bg-primary py-1',
                        isAnimating ? 'animate-bounce' : '',
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      <span className="text-typography">
                        {`+ ${selectedValues.length - maxCount} more`}
                      </span>
                      <XCircle
                        className="text-typography ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation()
                          clearExtraOptions()
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="h-full min-h-[50px] flex items-center justify-between">
                  <div className="min-h-[50px] w-[50px] h-auto px-4 flex items-center justify-center">
                    <XIcon
                      className="text-typography h-4 mx-2 cursor-pointer text-muted-foreground"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleClear()
                      }}
                    />
                  </div>
                  <Separator orientation="vertical" className="min-h-[50px] !h-full" />
                  <div
                    className={cn(
                      'min-h-[50px] w-[50px] h-auto px-4 flex items-center justify-center ',
                      isPopoverOpen ? 'border-l border-l-black' : 'border-l',
                    )}
                  >
                    <ChevronDown className="h-4 mx-2 cursor-pointer text-typography" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-grow bg-inputBackground rounded-xl flex-shrink flex items-center justify-between w-full h-full mx-auto ">
                <span className="text-sm text-muted mx-3">{placeholder}</span>
                <div
                  className={cn(
                    'min-h-[50px] w-[50px] h-full px-4 flex items-center justify-center',
                    isPopoverOpen ? 'border-l border-l-black' : 'border-l',
                  )}
                >
                  <ChevronDown className="h-4 cursor-pointer text-muted" />
                </div>
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          id="radix-popper"
          className="p-0 !border w-[--radix-popover-trigger-width] !border-typography bg-card !z-[99999]"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command className="!w-full bg-card">
            <CommandInput placeholder="Search..." onKeyDown={handleInputKeyDown} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup className="py-2">
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  className="group cursor-pointer hover:!bg-typography"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-typography group-hover:text-typography group-hover:border-checkBoxColor',
                      selectedValues.length === options.length
                        ? 'bg-typography text-checkBoxColor '
                        : 'opacity-50 [&_svg]:invisible',
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <span className="text-typography group-hover:text-checkBoxColor">
                    (Select All)
                  </span>
                </CommandItem>
                {options.map((option) => {
                  const isSelected = selectedValues.some((value) => value === option.id)
                  return (
                    <CommandItem
                      key={option.id}
                      onSelect={() => toggleOption(option.id)}
                      className="group cursor-pointer hover:!bg-typography"
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-typography group-hover:text-typography group-hover:border-checkBoxColor',
                          isSelected
                            ? 'bg-typography text-checkBoxColor group-hover:text-typography group-hover:bg-checkBoxColor'
                            : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {/* {option.icon && (
                        <option.icon className="mr-2 h-4 w-4 text-typography group-hover:text-checkBoxColor" />
                      )} */}
                      <span className="text-typography group-hover:text-checkBoxColor">
                        {option.name} - {option.ar_name}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
              <CommandSeparator />

              {/* Action buttons ( clear and cancel ) */}
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator orientation="vertical" className="flex min-h-6 h-full" />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {/* {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'cursor-pointer my-2 text-foreground bg-background w-3 h-3',
              isAnimating ? '' : 'text-muted-foreground',
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )} */}
      </Popover>
    )
  },
)

MultiCommuneSelect.displayName = 'MultiCommuneSelect'
